import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { vi, enGB } from 'date-fns/locale';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import CustomAdapter from '@src/components/CustomDatePickerRange/CustomDateAdapter';
import { LANGUAGE } from '@src/constants';
import i18n from '@src/languages';
import Typography from '@src/new-components/Typography';
import { StyledCommonInput } from '@src/new-components/Input/CommonInput/index.style';
import { COLOR } from '@src/styles/color';

const SingleDatePicker = ({
  name,
  label,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  placeholder = 'DD/MM/YYYY',
  format = 'DD/MM/YYYY',
  shouldDisableDate,
  sx,
  maxDate,
}) => {
  const { language } = i18n;
  const [open, setOpen] = useState(false);

  const pickerInputFormat = 'dd/MM/yyyy';

  const dateValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const momenDate = moment(value, format, true);
    return momenDate.isValid() ? momenDate.toDate() : null;
  }, [value, format]);

  const handleChange = (newDate) => {
    const formatted = newDate ? moment(newDate).format(format) : '';
    onChange?.({ target: { name, value: formatted } });
  };

  return (
    <StyledCommonInput>
      <Typography className="input-label">{label}</Typography>
      <LocalizationProvider
        dateAdapter={CustomAdapter}
        locale={language === LANGUAGE.VN ? vi : enGB}
      >
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={dateValue}
          onChange={handleChange}
          maxDate={maxDate}
          inputFormat={pickerInputFormat}
          disabled={disabled}
          shouldDisableDate={shouldDisableDate}
          PopperProps={{
            placement: 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              size="small"
              fullWidth
              className="input-field"
              placeholder={placeholder}
              error={error}
              onClick={() => setOpen(true)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '44px',
                  backgroundColor: COLOR.white,
                  paddingRight: '12px',
                },
                '.MuiOutlinedInput-input': {
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                },
                ...sx,
              }}
              InputProps={{
                ...params.InputProps,
                inputProps: {
                  ...params.inputProps,
                  readOnly: true,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setOpen(true)}>
                      <EventNoteOutlinedIcon sx={{ color: COLOR.indigo[80] }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </LocalizationProvider>
      {error && <Typography className="input-error">{helperText}</Typography>}
    </StyledCommonInput>
  );
};

export default SingleDatePicker;
